import React, { useEffect, useState } from 'react';
import { GlobalOutlined, UserOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  CheckboxChangeEvent,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  RadioGroupProps,
  Row,
  Select,
  SelectProps,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useUser } from '@hooks/useUser';
import { CANCEL_BUTTON_LABEL } from '@constants/common';
import { StatusCodes } from '@constants/enum/common';
import { EventControlApiKeysElement } from '@constants/event';
import {
  API_KEY_EXPIRES_AT_VALIDATION_ERROR,
  API_KEY_INITIAL_VALUES,
  API_KEY_TYPE_DESCRIPTIONS,
  API_KEY_TYPE_LABELS,
  ApiKeyType,
  CREATE_API_KEY_MODAL,
  DEFAULT_WORKSPACE_OPTION,
  OPTIONAL_FIELD_MARK,
  USER_ROLE_OPTIONS,
} from '@modules/APIKeys/constants/apiKeys';
import useApiKeys from '@modules/APIKeys/hooks/useApiKeys';
import { ICreateApiKeyPayload } from '@modules/APIKeys/types/apiKeys';
import styles from './apiKeyAddForm.module.scss';

interface IApiKeyAddFormProps {
  isOpen: boolean;
  onClose: (showApiKey?: boolean) => void;
}

type ApiKeyForm = ICreateApiKeyPayload & { never_expires: boolean };

const { Compact } = Space;
const { Item, useForm } = Form;
const { Text } = Typography;

const ApiKeyAddForm: React.FC<IApiKeyAddFormProps> = ({ isOpen, onClose }) => {
  const [form] = useForm<ApiKeyForm>();
  const { createApiKey, createApiKeyStatus, trackApiKeyUserEvent } = useApiKeys();
  const {
    user: { role: userRole },
    getAccessibleWorkspaces,
    isOrganizationAdmin,
  } = useUser();

  const [isOkButtonActive, setIsOkButtonActive] = useState<boolean>(false);
  const [isDatePickerDisable, setIsDatePickerDisabled] = useState<boolean>(false);
  const [descriptionText, setDescriptionText] = useState<string>(
    API_KEY_TYPE_DESCRIPTIONS[ApiKeyType.Personal],
  );

  const loading = createApiKeyStatus === StatusCodes.IN_PROGRESS;

  const workspaceOptions: SelectProps['options'] = [
    DEFAULT_WORKSPACE_OPTION,
    ...getAccessibleWorkspaces().map(({ name }) => ({
      label: name,
      value: name,
    })),
  ];

  const personalOptions: RadioGroupProps['options'] = [
    {
      label: (
        <Space size={4}>
          <UserOutlined /> {API_KEY_TYPE_LABELS[ApiKeyType.Personal]}
        </Space>
      ),
      value: true,
    },
    {
      label: (
        <Space size={4}>
          <GlobalOutlined /> {API_KEY_TYPE_LABELS[ApiKeyType.Service]}
        </Space>
      ),
      value: false,
    },
  ];

  const onWorkspacesChange = (value: string[]) => {
    if (value.length === 0) value = [DEFAULT_WORKSPACE_OPTION.value as string];

    if (value.length > 1) {
      value = value.filter((v) => v !== DEFAULT_WORKSPACE_OPTION.value);
    }

    form.setFieldsValue({ workspaces: value });
  };

  const onFormValuesChange = ({ personal }: ApiKeyForm) => {
    setDescriptionText(
      personal
        ? API_KEY_TYPE_DESCRIPTIONS[ApiKeyType.Personal]
        : API_KEY_TYPE_DESCRIPTIONS[ApiKeyType.Service],
    );
  };

  const onFinish = async (payload: ApiKeyForm) => {
    const { never_expires: neverExpires, ...data } = payload;
    data.expires = !neverExpires;
    try {
      await createApiKey(data);
      await trackApiKeyUserEvent({
        event: EventControlApiKeysElement.SAVE_KEY,
        properties: { ...data },
      });
    } finally {
      form.resetFields();
    }
  };

  const onCancel = () => onClose(false);

  const onExpiresCheckboxChecked = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      form.setFields([
        {
          name: 'expires_at',
          value: undefined,
          errors: [],
          warnings: [],
        },
      ]);
    }

    setIsOkButtonActive(isChecked);
    setIsDatePickerDisabled(isChecked);
    form.setFieldValue('never_expires', isChecked);
  };

  const validateExpiresAt = (_: unknown, value: string | undefined) => {
    if (!(form.getFieldValue('never_expires') || value)) {
      setIsOkButtonActive(false);
      return Promise.reject(API_KEY_EXPIRES_AT_VALIDATION_ERROR);
    }

    setIsOkButtonActive(true);

    return Promise.resolve();
  };

  const renderFooter = () => {
    return (
      <Flex justify="end" gap={8} className={styles.footer}>
        <Button onClick={onCancel}>{CANCEL_BUTTON_LABEL}</Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={loading}
          disabled={!isOkButtonActive}
        >
          {CREATE_API_KEY_MODAL.BUTTON_TEXT}
        </Button>
      </Flex>
    );
  };

  const renderOptionalFields = () => {
    if (!isOrganizationAdmin()) return null;

    return (
      <>
        <Item name="role" label={CREATE_API_KEY_MODAL.FORM_ITEMS.ROLE.LABEL}>
          <Select options={USER_ROLE_OPTIONS} />
        </Item>

        <Item name="workspaces" label={CREATE_API_KEY_MODAL.FORM_ITEMS.WORKSPACES.LABEL}>
          <Select mode="multiple" options={workspaceOptions} onChange={onWorkspacesChange} />
        </Item>
      </>
    );
  };

  useEffect(() => {
    if (createApiKeyStatus === StatusCodes.SUCCESS) {
      onClose(true);
    }
  }, [createApiKeyStatus]);

  return (
    <Modal
      title={CREATE_API_KEY_MODAL.TITLE}
      open={isOpen}
      onCancel={onCancel}
      footer={renderFooter()}
      width={520}
      className={styles.addForm}
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          ...API_KEY_INITIAL_VALUES,
          role: userRole,
        }}
        validateTrigger={['onChange', 'onBlur']}
        onValuesChange={onFormValuesChange}
      >
        <Item name="personal">
          <Radio.Group options={personalOptions} optionType="button" />
        </Item>

        <Item>
          <Text>{descriptionText}</Text>
        </Item>

        <Item
          name="name"
          label={
            <Compact>
              {CREATE_API_KEY_MODAL.FORM_ITEMS.NAME.LABEL}
              <Text className={styles.optionalMark}>{OPTIONAL_FIELD_MARK}</Text>
            </Compact>
          }
          extra={CREATE_API_KEY_MODAL.FORM_ITEMS.NAME.HELP_TEXT}
        >
          <Input placeholder={CREATE_API_KEY_MODAL.FORM_ITEMS.NAME.PLACEHOLDER} />
        </Item>

        <Item label={CREATE_API_KEY_MODAL.FORM_ITEMS.EXPIRES_AT.LABEL}>
          <Row gutter={8}>
            <Col span={16}>
              <Item
                name="expires_at"
                rules={[
                  {
                    validator: validateExpiresAt,
                    message: API_KEY_EXPIRES_AT_VALIDATION_ERROR,
                  },
                ]}
                noStyle
              >
                <DatePicker
                  className={styles.datePicker}
                  format="DD/MM/YYYY"
                  disabled={isDatePickerDisable}
                  disabledDate={(current) => current && current < dayjs().endOf('day')}
                  placeholder={CREATE_API_KEY_MODAL.FORM_ITEMS.EXPIRES_AT.PLACEHOLDER}
                />
              </Item>
            </Col>
            <Col span={8}>
              <Item name="never_expires" noStyle>
                <div className={styles.checkboxWrapper}>
                  <Checkbox onChange={onExpiresCheckboxChecked}>
                    {CREATE_API_KEY_MODAL.FORM_ITEMS.EXPIRES.LABEL}
                  </Checkbox>
                </div>
              </Item>
            </Col>
          </Row>
        </Item>

        {renderOptionalFields()}
      </Form>
    </Modal>
  );
};

export default ApiKeyAddForm;
